import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import EditUserDialog from '../EditUserDialog';
import FreePlayQuickDialog from '../FreePlayQuickDialog';
import GoodwillCreditDialog from '../GoodwillCreditDialog';
import { useMessagePlayer } from '../../contexts/MessagePlayerContext';
import { Plus, Minus, MessageCircle, Gift, Pencil, ExternalLink, Wallet, Phone, Mail, Calendar, ShieldCheck, Crown, HeartHandshake } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return '—'; }
};
const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return ''; }
};
const prettyType = (t) => String(t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Single-click customer panel launched from the admin home search.
 * Full info at a glance + every common action in one place.
 */
const CustomerPanel = ({ user, onClose, onOpenCredit, onOpenFull, onChanged }) => {
    const msg = useMessagePlayer();
    const [act, setAct] = useState(null);
    const [loadingAct, setLoadingAct] = useState(false);
    const [adjMode, setAdjMode] = useState(false);
    const [adjAmt, setAdjAmt] = useState('');
    const [adjReason, setAdjReason] = useState('Correcting error');
    const [adjBusy, setAdjBusy] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [bonusBump, setBonusBump] = useState(0);

    useEffect(() => { setBonusBump(0); }, [user?.id]);

    useEffect(() => {
        if (!user?.id) { setAct(null); return; }
        let alive = true;
        setLoadingAct(true);
        axios.get(`${API}/admin/user-activity/${user.id}`, { withCredentials: true })
            .then(({ data }) => { if (alive) setAct(data); })
            .catch(() => { if (alive) setAct(null); })
            .finally(() => { if (alive) setLoadingAct(false); });
        return () => { alive = false; };
    }, [user?.id, refreshKey]);

    if (!user) return null;

    const doAct = (fn) => () => { onClose?.(); fn?.(); };
    const statusBad = user.status && user.status !== 'active';
    const lin = Number(act?.lifetime_loaded || 0);
    const lout = Number(act?.lifetime_cashed || 0);
    const lnet = lin - lout;

    const ADJ_REASONS = ['Correcting error', 'Mistaken credit', 'Chargeback / reversal', 'Promo clawback', 'Bonus reversal', 'Duplicate load', 'Other'];
    const submitAdjust = async () => {
        const amt = parseFloat(adjAmt);
        if (!amt || amt <= 0) { toast.error('Enter an amount greater than 0'); return; }
        if (amt > Number(user.credits || 0)) { toast.error('Amount exceeds the player balance'); return; }
        setAdjBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/credits`,
                { user_id: user.id, amount: amt, operation: 'adjust', reason: adjReason },
                { withCredentials: true });
            toast.success(`Removed ${fmt$(amt)} — ${adjReason} · new balance ${fmt$(data?.new_balance)}`);
            setAdjMode(false); setAdjAmt('');
            onChanged?.();
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Adjustment failed');
        } finally { setAdjBusy(false); }
    };

    return (
        <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-lg" data-testid="customer-panel">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#FFD700]">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="truncate" data-testid="customer-panel-name">{user.name || user.email || 'Customer'}</span>
                        {statusBad && (
                            <span className="text-[10px] uppercase font-black tracking-wider bg-[#FF3B30]/20 text-[#FF3B30] rounded px-1.5 py-0.5">
                                {user.status}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                {/* Balance + key info */}
                <div className="rounded-xl border border-white/10 bg-black/40 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[#A0AAB2] text-xs uppercase tracking-wider">
                            <Wallet className="w-4 h-4 text-[#39FF14]" /> Balance
                        </div>
                        <div className="text-2xl font-black tabular-nums text-[#39FF14]" data-testid="customer-panel-credits">
                            {fmt$(user.credits)}
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-xs">
                        <span className="text-[#A0AAB2] uppercase tracking-wider">Free Play</span>
                        <span className="font-bold tabular-nums text-pink-300" data-testid="customer-panel-freeplay-balance">
                            {fmt$(Number(user.bonus_credits || 0) + bonusBump)}
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-1.5 text-sm">
                        <div className="flex items-center gap-2 text-[#cfd6db]"><Mail className="w-3.5 h-3.5 text-[#A0AAB2]" />{user.email || '—'}</div>
                        <div className="flex items-center gap-2 text-[#cfd6db]"><Phone className="w-3.5 h-3.5 text-[#A0AAB2]" />{user.phone || '—'}</div>
                        <div className="flex items-center gap-2 text-[#cfd6db]"><Calendar className="w-3.5 h-3.5 text-[#A0AAB2]" />Joined {fmtDate(user.created_at)}</div>
                        {act?.referred_by_ambassador && (
                            <div className="space-y-0.5" data-testid="customer-panel-referred-by">
                                <div className="flex items-center gap-2 text-[#FFD700] flex-wrap">
                                    <Crown className="w-3.5 h-3.5 text-[#FFD700]" />
                                    <span className="text-[#A0AAB2]">Referred by</span>
                                    <span className="font-semibold">
                                        {act.referred_by_ambassador.ambassador_name || act.referred_by_ambassador.ambassador_code}
                                    </span>
                                    {act.referred_by_ambassador.ambassador_code && (
                                        <span className="text-[#5f6b73] font-mono">({act.referred_by_ambassador.ambassador_code})</span>
                                    )}
                                    {act.referred_by_ambassador.is_house && (
                                        <span className="px-1.5 py-0.5 rounded bg-[#5b4a16] text-[#FFD700] text-[9px] font-bold uppercase tracking-wide"
                                            data-testid="customer-panel-house-badge">House</span>
                                    )}
                                </div>
                                {(act.referred_by_ambassador.net_house_profit != null) && (
                                    <div className="flex items-center gap-2 pl-5 text-[11px] text-[#A0AAB2]">
                                        <span>Net to house <span className="text-[#cfd6db] font-mono">${(act.referred_by_ambassador.net_house_profit || 0).toFixed(2)}</span></span>
                                        <span className="text-[#3a4046]">·</span>
                                        <span>Earned <span className="text-[#34d399] font-mono">${(act.referred_by_ambassador.commission_earned || 0).toFixed(2)}</span> ({act.referred_by_ambassador.commission_pct || 0}%)</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Lifetime In / Out / Net */}
                <div className="grid grid-cols-3 gap-2" data-testid="customer-panel-lifetime">
                    <div className="rounded-lg border border-[#39FF14]/25 bg-[#39FF14]/5 p-2 text-center">
                        <p className="text-[9px] uppercase tracking-wider text-[#A0AAB2]">Lifetime In</p>
                        <p className="text-sm font-black tabular-nums text-[#39FF14]">{loadingAct ? '…' : fmt$(lin)}</p>
                    </div>
                    <div className="rounded-lg border border-[#FF6B00]/25 bg-[#FF6B00]/5 p-2 text-center">
                        <p className="text-[9px] uppercase tracking-wider text-[#A0AAB2]">Lifetime Out</p>
                        <p className="text-sm font-black tabular-nums text-[#FF6B00]">{loadingAct ? '…' : fmt$(lout)}</p>
                    </div>
                    <div className="rounded-lg border border-white/15 bg-black/40 p-2 text-center">
                        <p className="text-[9px] uppercase tracking-wider text-[#A0AAB2]">Net</p>
                        <p className="text-sm font-black tabular-nums" style={{ color: lnet >= 0 ? '#39FF14' : '#FF6B00' }}>
                            {loadingAct ? '…' : `${lnet >= 0 ? '+' : '-'}${fmt$(Math.abs(lnet))}`}
                        </p>
                    </div>
                </div>

                {/* Recent activity (last 5) */}
                <div className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid="customer-panel-activity">
                    <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] mb-1.5">Recent Activity</p>
                    {loadingAct && <p className="text-xs text-[#5f6b73]">Loading…</p>}
                    {!loadingAct && (!act?.transactions || act.transactions.length === 0) && (
                        <p className="text-xs text-[#5f6b73]">No recent transactions.</p>
                    )}
                    <div className="space-y-1">
                        {(act?.transactions || []).map((t, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-[#cfd6db] truncate mr-2">{prettyType(t.type)}<span className="text-[#5f6b73]"> · {fmtTime(t.created_at)}</span></span>
                                <span className={`tabular-nums font-bold ${Number(t.amount) >= 0 ? 'text-[#39FF14]' : 'text-[#FF6B00]'}`}>
                                    {Number(t.amount) >= 0 ? '+' : '-'}{fmt$(Math.abs(Number(t.amount)))}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2 mt-1">
                    <Button onClick={doAct(() => onOpenCredit?.(user, 'deposit'))}
                        className="bg-[#39FF14]/15 hover:bg-[#39FF14]/25 text-[#39FF14] border border-[#39FF14]/30"
                        data-testid="customer-panel-add-credits">
                        <Plus className="w-4 h-4 mr-1" /> Add Credits
                    </Button>
                    <Button onClick={doAct(() => onOpenCredit?.(user, 'withdraw'))}
                        disabled={!user.credits || user.credits <= 0}
                        className="bg-[#FF6B00]/15 hover:bg-[#FF6B00]/25 text-[#FF6B00] border border-[#FF6B00]/30"
                        data-testid="customer-panel-remove-credits">
                        <Minus className="w-4 h-4 mr-1" /> Remove Credits
                    </Button>

                    <Button onClick={doAct(() => msg.open(user))}
                        variant="outline" className="border-white/15 text-white hover:bg-white/5"
                        data-testid="customer-panel-message">
                        <MessageCircle className="w-4 h-4 mr-1" /> Message
                    </Button>

                    <FreePlayQuickDialog user={user} onGranted={onChanged} trigger={
                        <Button variant="outline" className="border-white/15 text-white hover:bg-white/5"
                            data-testid="customer-panel-freeplay">
                            <Gift className="w-4 h-4 mr-1" /> Free Play
                        </Button>
                    } />

                    <GoodwillCreditDialog player={user} onDone={(d) => {
                        setBonusBump(Number(d?.bonus_balance ?? 0) - Number(user.bonus_credits || 0));
                        setRefreshKey((k) => k + 1); onChanged?.();
                    }} trigger={
                        <Button variant="outline" className="border-pink-400/30 text-pink-200 hover:bg-pink-500/10"
                            data-testid="customer-panel-goodwill">
                            <HeartHandshake className="w-4 h-4 mr-1" /> Goodwill
                        </Button>
                    } />

                    <EditUserDialog user={user} onUpdated={onChanged} trigger={
                        <Button variant="outline" className="border-white/15 text-white hover:bg-white/5"
                            data-testid="customer-panel-edit">
                            <Pencil className="w-4 h-4 mr-1" /> Edit Info
                        </Button>
                    } />

                    <Button onClick={doAct(() => onOpenFull?.(user))}
                        variant="outline" className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
                        data-testid="customer-panel-open-full">
                        <ExternalLink className="w-4 h-4 mr-1" /> Full Record
                    </Button>
                </div>

                {/* Admin credit adjustment — audited debit that is NOT a cashout. */}
                <div className="mt-1">
                    {!adjMode ? (
                        <button type="button" onClick={() => setAdjMode(true)}
                            className="w-full text-xs text-[#FF6B00]/90 hover:text-[#FF6B00] underline underline-offset-2"
                            data-testid="customer-panel-adjust-open">
                            Remove credits as an adjustment (fix a mistake — no cashout)
                        </button>
                    ) : (
                        <div className="rounded-xl border border-[#FF6B00]/30 bg-[#FF6B00]/5 p-3 space-y-2" data-testid="customer-panel-adjust-form">
                            <p className="text-[11px] uppercase tracking-wider text-[#FF6B00] font-bold">Credit Adjustment (no cashout)</p>
                            <div className="flex gap-2">
                                <input type="number" min="0" step="0.01" value={adjAmt} onChange={(e) => setAdjAmt(e.target.value)}
                                    placeholder="Amount" data-testid="customer-panel-adjust-amount"
                                    className="w-28 bg-black/40 border border-white/15 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:border-[#FF6B00]" />
                                <select value={adjReason} onChange={(e) => setAdjReason(e.target.value)}
                                    data-testid="customer-panel-adjust-reason"
                                    className="flex-1 bg-black/40 border border-white/15 rounded-md px-2 py-1.5 text-sm text-white outline-none focus:border-[#FF6B00]">
                                    {ADJ_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div className="flex gap-2 justify-end">
                                <Button variant="outline" className="border-white/15 text-white/70 h-8 px-3" onClick={() => setAdjMode(false)} disabled={adjBusy}>Cancel</Button>
                                <Button className="bg-[#FF6B00]/20 hover:bg-[#FF6B00]/30 text-[#FF6B00] border border-[#FF6B00]/40 h-8 px-3"
                                    onClick={submitAdjust} disabled={adjBusy} data-testid="customer-panel-adjust-confirm">
                                    {adjBusy ? 'Removing…' : 'Remove credits'}
                                </Button>
                            </div>
                            <p className="text-[10px] text-white/40">Logged as an Admin Adjustment (audited). Does not enter the cashout/approval queue.</p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CustomerPanel;
